import { ApplicantType, RelationshipType } from '@/helpers/const';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import type { ApplicantWithRelationships, ApplicantId } from '@/types';
import { computed } from 'vue';

/**
 * Composable for managing applicant permissions in the declaration flow.
 * Handles visibility and editing rules for Income, Expenses, and Assets steps.
 */
export function useApplicantPermissions() {
  const { authorisedUser, loanApp } = useDeclarationProcess();

  const currentUserId = computed(() => authorisedUser.id as ApplicantId);
  const currentUser = computed(() => loanApp.value.applicants.find((a) => a.id === currentUserId.value));
  const isPrimaryUser = computed(() => currentUser.value?.type === ApplicantType.PRIMARY);
  const isBrokerUser = computed(() => loanApp.value.userType === 'Broker');

  /**
   * Find the partner of a given applicant using applicantRelationships.
   * Returns the partner if they have a married_or_defacto relationship.
   */
  const findPartner = (applicantId: ApplicantId): ApplicantWithRelationships | undefined => {
    const applicant = loanApp.value.applicants.find((a) => a.id === applicantId);
    if (!applicant) return undefined;

    const marriedRelationship = applicant.applicantRelationships?.find(
      (rel) => rel.relationship === RelationshipType.MARRIED_OR_DEFACTO,
    );

    if (marriedRelationship) {
      return loanApp.value.applicants.find((a) => a.id === marriedRelationship.applicantId);
    }

    return undefined;
  };

  const currentUserPartner = computed(() => findPartner(currentUserId.value));

  // ============================================================================
  // INCOME PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants visible for the Income step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's
   */
  const getVisibleApplicantsForIncome = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id);
    }

    const visibleIds: ApplicantId[] = [currentUserId.value];
    if (currentUserPartner.value) {
      visibleIds.push(currentUserPartner.value.id);
    }

    return visibleIds;
  });

  /**
   * Check if current user can edit a specific applicant's income.
   * - Broker: can edit all
   * - Can always edit own income
   * - Primary can edit partner's income
   * - Non-primary: can only edit own (partner's is view-only)
   */
  const canEditApplicantIncome = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (applicantId === currentUserId.value) return true;
    if (isPrimaryUser.value && currentUserPartner.value?.id === applicantId) {
      return true;
    }
    return false;
  };

  /**
   * Get completion status for a specific applicant's income.
   */
  const getApplicantIncomeStatus = (applicantId: ApplicantId) => {
    const applicant = loanApp.value.applicants.find((a) => a.id === applicantId);
    return {
      id: applicantId,
      name: applicant?.preferredName || applicant?.firstName || '',
      isCompleted: (loanApp.value.financials[applicantId]?.income?.employment?.length ?? 0) > 0,
    };
  };

  // ============================================================================
  // EXPENSES PERMISSIONS (Household-based)
  // ============================================================================

  /**
   * Check if an applicant has already submitted their expenses.
   */
  const hasSubmittedExpenses = (applicantId: ApplicantId): boolean => {
    const financials = loanApp.value.financials;
    return financials[applicantId]?.expenses?.numberOfDependants !== undefined;
  };

  /**
   * Get the household owner for expenses.
   * For married couples, expenses are stored under primary or the first submitter.
   */
  const getHouseholdExpenseOwnerId = computed((): ApplicantId => {
    if (currentUserPartner.value) {
      const primaryApplicant = loanApp.value.applicants.find((a) => a.type === ApplicantType.PRIMARY);
      if (
        primaryApplicant &&
        (primaryApplicant.id === currentUserId.value || primaryApplicant.id === currentUserPartner.value.id)
      ) {
        return primaryApplicant.id;
      }
      const partnerId = currentUserPartner.value.id;
      if (hasSubmittedExpenses(partnerId)) {
        return partnerId;
      }
    }
    return currentUserId.value;
  });

  /**
   * Check if current user can edit household expenses.
   * - Broker: can always edit
   * - Single / married to non-applicant: can edit own household
   * - Married to applicant: can edit if partner hasn't submitted yet
   */
  const canEditHouseholdExpenses = computed((): boolean => {
    if (isBrokerUser.value) return true;
    if (!currentUserPartner.value) return true;

    const partnerId = currentUserPartner.value.id;
    if (hasSubmittedExpenses(partnerId)) return false;

    return true;
  });

  /**
   * Get the applicant IDs for expenses display.
   * - Broker: sees one entry per household
   * - Regular users: see the household owner
   */
  const getHouseholdApplicantForExpenses = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      const seen = new Set<ApplicantId>();
      const householdIds: ApplicantId[] = [];

      for (const applicant of loanApp.value.applicants) {
        if (seen.has(applicant.id)) continue;

        const partner = findPartner(applicant.id);
        if (partner) {
          const primaryOfPair =
            applicant.type === ApplicantType.PRIMARY
              ? applicant
              : partner.type === ApplicantType.PRIMARY
              ? partner
              : applicant;
          if (!seen.has(primaryOfPair.id)) {
            householdIds.push(primaryOfPair.id);
            seen.add(primaryOfPair.id);
            seen.add(partner.id);
          }
        } else {
          householdIds.push(applicant.id);
          seen.add(applicant.id);
        }
      }
      return householdIds;
    }

    return [getHouseholdExpenseOwnerId.value];
  });

  /**
   * Check if current user is viewing partner's submitted expenses (view-only mode).
   */
  const isViewingPartnerExpenses = computed((): boolean => {
    if (!currentUserPartner.value) return false;
    return hasSubmittedExpenses(currentUserPartner.value.id) && !canEditHouseholdExpenses.value;
  });

  // ============================================================================
  // ASSETS & LIABILITIES PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants to display in Assets step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's (partner's card without edit button)
   */
  const getApplicantsForAssetsDisplay = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id);
    }

    const visibleIds: ApplicantId[] = [currentUserId.value];
    if (currentUserPartner.value) {
      visibleIds.push(currentUserPartner.value.id);
    }

    return visibleIds;
  });

  /**
   * Check if current user can edit a specific applicant's assets.
   * - Broker: can edit all
   * - All others: can only edit own assets/liabilities
   */
  const canEditApplicantAssets = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    return applicantId === currentUserId.value;
  };

  /**
   * Check if current user can view a specific applicant's assets data.
   * Users can see their own data and their partner's data (read-only).
   */
  const canViewApplicantAssetsData = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (applicantId === currentUserId.value) return true;
    if (currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  return {
    isBrokerUser,
    currentUserPartner,
    // Income
    getVisibleApplicantsForIncome,
    canEditApplicantIncome,
    getApplicantIncomeStatus,
    // Expenses
    getHouseholdExpenseOwnerId,
    canEditHouseholdExpenses,
    getHouseholdApplicantForExpenses,
    isViewingPartnerExpenses,
    // Assets
    getApplicantsForAssetsDisplay,
    canEditApplicantAssets,
    canViewApplicantAssetsData,
  };
}
